import React, { useEffect, useRef } from 'react';

const isAllowedChar = (char, otpType) => {
  if (otpType === 'alpha') {
    return /^[A-Za-z\u0600-\u06FF]$/.test(char);
  }
  if (otpType === 'alpha-numeric' || otpType === 'alphanumeric' || otpType === 'any') {
    return /^[A-Za-z0-9\u0600-\u06FF]$/.test(char);
  }
  return /^[0-9]$/.test(char);
};

const sanitizeValue = (raw, otpType, length) => {
  const next = String(raw || '')
    .split('')
    .filter((char) => isAllowedChar(char, otpType))
    .join('');
  const sliced = next.slice(0, length);
  return otpType === 'alpha' ? sliced.toUpperCase() : sliced;
};

const OTPInput = ({
  OTPLength = 4,
  value = '',
  onChange,
  otpType = 'number',
  inputStyles = {},
  disabled = false,
  onKeyDown,
  id,
  autoFocus = false,
}) => {
  const inputsRef = useRef([]);
  const valueStr = sanitizeValue(value, otpType, OTPLength);

  useEffect(() => {
    if (autoFocus && !disabled) {
      const el = inputsRef.current[0];
      if (el) {
        el.focus();
        el.select();
      }
    }
  }, [autoFocus, disabled]);

  const prevLenRef = useRef(valueStr.length);

  const emit = (next) => {
    if (onChange) {
      onChange(sanitizeValue(next, otpType, OTPLength));
    }
  };

  const focusIndex = (index) => {
    const nextIndex = Math.max(0, Math.min(OTPLength - 1, index));
    const el = inputsRef.current[nextIndex];
    if (el && !disabled) {
      el.focus();
      el.select();
    }
  };

  useEffect(() => {
    if (disabled) {
      return;
    }
    const target =
      valueStr.length < prevLenRef.current
        ? Math.max(0, valueStr.length)
        : Math.min(valueStr.length, OTPLength - 1);
    if (valueStr.length !== prevLenRef.current) {
      const el = inputsRef.current[target];
      if (el) {
        el.focus();
        el.select();
      }
    }
    prevLenRef.current = valueStr.length;
  }, [valueStr, OTPLength, disabled]);

  const handleChange = (index, event) => {
    const raw = event.target.value;
    if (!raw) {
      emit(valueStr.slice(0, index) + valueStr.slice(index + 1));
      return;
    }

    const sanitized = sanitizeValue(raw, otpType, OTPLength);
    if (!sanitized) {
      emit(valueStr);
      return;
    }

    if (sanitized.length > 1) {
      const next = sanitizeValue(valueStr.slice(0, index) + sanitized, otpType, OTPLength);
      emit(next);
      focusIndex(Math.min(next.length, OTPLength - 1));
      return;
    }

    const chars = valueStr.split('');
    chars[index] = sanitized;
    const next = sanitizeValue(chars.join(''), otpType, OTPLength);
    emit(next);
    if (next.length > index) {
      focusIndex(index + 1);
    }
  };

  const handleKeyDown = (index, event) => {
    if (onKeyDown) {
      onKeyDown(event);
    }
    if (event.defaultPrevented) {
      return;
    }

    if (event.key === 'Backspace') {
      event.preventDefault();
      if (valueStr[index]) {
        emit(valueStr.slice(0, index) + valueStr.slice(index + 1));
      } else {
        emit(valueStr.slice(0, -1));
        focusIndex(index - 1);
      }
      return;
    }

    if (event.key === 'Delete') {
      event.preventDefault();
      emit(valueStr.slice(0, index) + valueStr.slice(index + 1));
      return;
    }

    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      focusIndex(index - 1);
      return;
    }

    if (event.key === 'ArrowRight') {
      event.preventDefault();
      focusIndex(index + 1);
      return;
    }

    if (event.key.length === 1 && isAllowedChar(event.key, otpType)) {
      event.preventDefault();
      const char = otpType === 'alpha' ? event.key.toUpperCase() : event.key;
      const chars = valueStr.split('');
      chars[index] = char;
      const next = sanitizeValue(chars.join(''), otpType, OTPLength);
      emit(next);
      focusIndex(index + 1);
    }
  };

  const handlePaste = (index, event) => {
    event.preventDefault();
    const pasted = event.clipboardData.getData('text');
    const next = sanitizeValue(valueStr.slice(0, index) + pasted, otpType, OTPLength);
    emit(next);
    focusIndex(Math.min(next.length, OTPLength - 1));
  };

  return (
    <div
      id={id}
      dir="ltr"
      style={{
        display: 'flex',
        flexDirection: 'row',
        direction: 'ltr',
        unicodeBidi: 'isolate',
        justifyContent: 'center',
      }}
    >
      {Array.from({ length: OTPLength }).map((_, index) => (
        <input
          // eslint-disable-next-line react/no-array-index-key
          key={`otp-box-${index}`}
          ref={(el) => {
            inputsRef.current[index] = el;
          }}
          value={valueStr[index] || ''}
          disabled={disabled}
          inputMode={otpType === 'number' ? 'numeric' : 'text'}
          autoComplete={index === 0 ? 'one-time-code' : 'off'}
          maxLength={OTPLength}
          onChange={(event) => handleChange(index, event)}
          onKeyDown={(event) => handleKeyDown(index, event)}
          onPaste={(event) => handlePaste(index, event)}
          onFocus={(event) => event.target.select()}
          style={{
            width: '40px',
            height: '40px',
            margin: '0 4px',
            textAlign: 'center',
            fontSize: '1.4rem',
            direction: 'ltr',
            unicodeBidi: 'bidi-override',
            border: '1px solid #ced4da',
            borderRadius: '4px',
            outline: 'none',
            ...inputStyles,
          }}
        />
      ))}
    </div>
  );
};

export default OTPInput;
